// File: apps/web/src/pages/signup/SignupSuccessPage.tsx
// Version: 2026.4.7 | Path: apps/web/src/pages/signup/SignupSuccessPage.tsx
//
// CHANGE LOG (2026.4.7):
//   - Switched from subdomain-based to path-based tenancy. The previous version
//     read `ft_signup_subdomain` from localStorage and constructed
//     `https://{subdomain}.forgetrack.io/app/dashboard`, which doesn't exist
//     in this deployment — there are no per-tenant subdomains and the dashboard
//     route is `/dashboard`, not `/app/dashboard`. Tenant context comes from
//     the JWT and the X-Tenant-Domain header injected by @forgetrack/api-client,
//     not from the URL.
//   - Mirrors the redirectToApp() helper in routes/signup.tsx so both flows
//     resolve to the exact same target. If you change one, change the other.

import { useEffect, useState } from 'react';
import { motion } from 'motion/react';

const C = {
  bg:       '#050910',
  surface:  'rgba(8,16,32,0.88)',
  neon:     '#34d399',
  cyan:     '#22d3ee',
  violet:   '#a78bfa',
  text:     '#e8ecf2',
  textSoft: 'rgba(255,255,255,0.78)',
  muted:    'rgba(255,255,255,0.6)',
  dim:      'rgba(255,255,255,0.42)',
  faint:    'rgba(255,255,255,0.10)',
  neonGlow: 'rgba(52,211,153,0.5)',
};

const F = {
  display: "'Outfit', system-ui, sans-serif",
  mono:    "'JetBrains Mono', monospace",
};

const KEYFRAMES = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800&family=JetBrains+Mono:wght@400;600&display=swap');
  @keyframes ping-ring {
    0%   { transform: scale(1); opacity: 0.8; }
    100% { transform: scale(2.5); opacity: 0; }
  }
  @keyframes orbit {
    0%   { transform: rotate(0deg) translateX(48px) rotate(0deg); }
    100% { transform: rotate(360deg) translateX(48px) rotate(-360deg); }
  }
  @keyframes counter-up {
    0%   { opacity: 0; transform: translateY(8px); }
    100% { opacity: 1; transform: translateY(0); }
  }
`;

// Resolve the workspace base URL and dashboard target from the current host.
// Path-based tenancy: everything lives under app.{rootDomain}/...
// Mirrors routes/signup.tsx → redirectToApp().
function resolveWorkspaceUrls() {
  const host = window.location.host;
  const isLocal = host.includes('localhost') || host.includes('127.0.0.1');

  if (isLocal) {
    return {
      workspaceUrl: `${window.location.protocol}//${host}`,
      dashboardUrl: '/dashboard',
    };
  }

  const parts = host.split('.');
  const rootDomain = parts.length >= 2 ? parts.slice(-2).join('.') : host;
  return {
    workspaceUrl: `https://app.${rootDomain}`,
    dashboardUrl: `https://app.${rootDomain}/dashboard`,
  };
}

export default function SignupSuccessPage() {
  const [tenantUrl, setTenantUrl] = useState('');
  const [dashboardUrl, setDashboardUrl] = useState('/dashboard');
  const [countdown, setCountdown] = useState(5);
  const [verifyStatus, setVerifyStatus] = useState<'verifying' | 'paid' | 'pending' | 'error'>('verifying');

  useEffect(() => {
    const { workspaceUrl, dashboardUrl: resolvedDashboard } = resolveWorkspaceUrls();
    setTenantUrl(workspaceUrl);
    setDashboardUrl(resolvedDashboard);

    // Extract session_id from URL
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      // No session_id — skip verification, show success (backwards compat)
      setVerifyStatus('paid');
      startCountdown(resolvedDashboard);
      return;
    }

    // Verify checkout with the API
    let cancelled = false;
    const verify = async (attempt: number) => {
      try {
        const resp = await fetch(`/api/v1/billing/checkout-status/${encodeURIComponent(sessionId)}`, {
          credentials: 'include',
        });
        if (cancelled) return;

        if (resp.ok) {
          const data = await resp.json();
          if (data.status === 'paid') {
            setVerifyStatus('paid');
            startCountdown(resolvedDashboard);
          } else if (attempt < 2) {
            // Webhook may still be in flight — retry once after 2s
            setTimeout(() => { if (!cancelled) verify(attempt + 1); }, 2000);
          } else {
            // After 2 retries, show success anyway — webhook will catch up
            setVerifyStatus('paid');
            startCountdown(resolvedDashboard);
          }
        } else {
          // API error — show success anyway (don't block on verification failure)
          setVerifyStatus('paid');
          startCountdown(resolvedDashboard);
        }
      } catch {
        if (!cancelled) {
          setVerifyStatus('paid');
          startCountdown(resolvedDashboard);
        }
      }
    };

    verify(0);
    return () => { cancelled = true; };
  }, []);

  function startCountdown(target: string) {
    const id = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(id);
          window.location.href = target;
        }
        return c - 1;
      });
    }, 1000);
  }

  if (verifyStatus === 'verifying') {
    return (
      <div style={{
        minHeight: '100vh', backgroundColor: C.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: F.display,
      }}>
        <div style={{
          fontFamily: F.mono, fontSize: 12, color: C.muted,
          letterSpacing: '0.12em', textTransform: 'uppercase',
        }}>
          Verifying payment...
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{KEYFRAMES}</style>
      <div style={{
        minHeight: '100vh', backgroundColor: C.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: F.display, padding: '24px',
        background: `radial-gradient(ellipse at center top, rgba(52,211,153,0.08) 0%, ${C.bg} 60%)`,
      }}>
        <div style={{ textAlign: 'center', maxWidth: 520 }}>

          {/* Animated success orb */}
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 40 }}>
            {/* Outer ping rings */}
            {[0, 0.4, 0.8].map((delay, i) => (
              <div key={i} style={{
                position: 'absolute', width: 80, height: 80, borderRadius: '50%',
                border: `1px solid ${C.neon}`, opacity: 0,
                animation: `ping-ring 2s ease-out ${delay}s infinite`,
              }} />
            ))}
            {/* Orbiting dot */}
            <div style={{
              position: 'absolute', width: 10, height: 10, borderRadius: '50%',
              backgroundColor: C.cyan,
              boxShadow: `0 0 12px rgba(34,211,238,0.8)`,
              animation: 'orbit 3s linear infinite',
            }} />
            {/* Core */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              style={{
                width: 72, height: 72, borderRadius: '50%',
                background: `radial-gradient(circle, rgba(52,211,153,0.3), rgba(52,211,153,0.05))`,
                border: `1px solid ${C.neon}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28,
              }}
            >
              ✓
            </motion.div>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6, ease: [0.16,1,0.3,1] }}
            style={{
              fontFamily: F.display, fontSize: 32, fontWeight: 800,
              color: C.text, marginBottom: 12, letterSpacing: '-0.02em',
            }}
          >
            Engine activated.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45 }}
            style={{
              fontFamily: F.display, fontSize: 16, color: C.muted,
              lineHeight: 1.6, marginBottom: 36,
            }}
          >
            Your ForgeTrack workspace is ready. Start tracking your
            AI-augmented R&D and generate your first compliance report.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            style={{
              background: C.surface, border: `1px solid ${C.faint}`,
              borderRadius: 12, padding: '16px 24px', marginBottom: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontFamily: F.mono, fontSize: 9, color: C.dim, letterSpacing: '0.1em', marginBottom: 4 }}>
                YOUR WORKSPACE
              </div>
              <div style={{ fontFamily: F.mono, fontSize: 13, color: C.neon }}>
                {tenantUrl}
              </div>
            </div>
            <button
              onClick={() => navigator.clipboard.writeText(tenantUrl)}
              style={{
                background: 'rgba(52,211,153,0.1)', border: `1px solid ${C.neon}44`,
                borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                fontFamily: F.mono, fontSize: 10, color: C.neon, letterSpacing: '0.06em',
              }}
            >
              COPY
            </button>
          </motion.div>

          <motion.a
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            href={dashboardUrl}
            style={{
              display: 'block', fontFamily: F.display, fontSize: 15, fontWeight: 700,
              color: C.bg, textDecoration: 'none',
              background: `linear-gradient(135deg, ${C.neon}, ${C.cyan})`,
              borderRadius: 12, padding: '16px 48px',
              boxShadow: `0 8px 32px ${C.neonGlow}`,
            }}
          >
            → Launch ForgeTrack
          </motion.a>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            style={{
              fontFamily: F.mono, fontSize: 10, color: C.dim,
              marginTop: 20, letterSpacing: '0.06em',
            }}
          >
            Auto-redirecting in {countdown}s
          </motion.p>
        </div>
      </div>
    </>
  );
}
